<template>
  <div id="app" :class="{ 'pc-viewport': isPC }">
    <div v-if="isPC" class="pc-stage">
      <div class="pc-scale" :style="{ transform: `scale(${pcScale})` }">
        <div class="pc-container">
          <router-view v-slot="{ Component, route }">
            <transition name="fade" mode="out-in">
              <component :is="Component" :key="route.path" />
            </transition>
          </router-view>
          <TabBar v-if="showTabBar" />
          <Toast />
          <ConfirmDialog />
          <AnnouncementDialog 
            v-model:visible="announcementVisible" 
            :announcement="currentAnnouncement"
            @close="handleAnnouncementClose"
          />
        </div>
      </div>
    </div>
    <template v-else>
      <router-view v-slot="{ Component, route }">
        <transition name="fade" mode="out-in">
          <component :is="Component" :key="route.path" />
        </transition>
      </router-view>
      <TabBar v-if="showTabBar" />
      <Toast />
      <ConfirmDialog />
      <AnnouncementDialog 
        v-model:visible="announcementVisible" 
        :announcement="currentAnnouncement"
        @close="handleAnnouncementClose"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import TabBar from '@/components/TabBar.vue'
import Toast from '@/components/Toast.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import AnnouncementDialog from '@/components/AnnouncementDialog.vue'
import bgmManager from '@/utils/bgm'
import { initSocket, disconnectSocket, getSocket } from '@/utils/socket.js'
import { useToast } from '@/composables/useToast.js'
import { getPublishedAnnouncements } from '@/api/announcement'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const toast = useToast()

// 检测是否为PC访问
const isPC = ref(false)
const pcScale = ref(1)

const PC_VIEWPORT_WIDTH = 390
const PC_VIEWPORT_HEIGHT = 844

// 检测设备类型
function detectDevice() {
  // 检测用户代理
  const userAgent = navigator.userAgent || navigator.vendor || window.opera
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase())
  
  // 检测屏幕宽度（PC通常宽度大于768px）
  const isWideScreen = window.innerWidth > 768
  
  // 如果是PC（非移动设备且屏幕较宽）
  isPC.value = !isMobile && isWideScreen
  updatePcScale()
}

function updatePcScale() {
  if (!isPC.value) {
    pcScale.value = 1
    return
  }

  const padding = 24
  const scaleByWidth = (window.innerWidth - padding) / PC_VIEWPORT_WIDTH
  const scaleByHeight = (window.innerHeight - padding) / PC_VIEWPORT_HEIGHT
  const nextScale = Math.min(1, scaleByWidth, scaleByHeight)

  // 防止极端窗口下缩放为0或负值
  pcScale.value = Math.max(0.4, nextScale)
}

// 监听窗口大小变化
function handleResize() {
  detectDevice()
  updatePcScale()
}

// 公告相关
const announcementVisible = ref(false)
const currentAnnouncement = ref({})
const announcementQueue = ref([])
const shownAnnouncementIds = ref(new Set())

const showTabBar = computed(() => {
  // 登录和注册页面不显示底部导航栏
  if (route.meta.isAuthPage) {
    return false
  }
  return route.meta.showTabBar === true
})

// 监听重连失败事件
const handleReconnectFailed = (event) => {
  console.error('WebSocket重连失败:', event.detail?.message)
  toast.error(event.detail?.message || '连接失败，请重新登录')
}

// 监听需要重新登录事件
const handleRequireRelogin = (event) => {
  console.warn('需要重新登录:', event.detail?.message)
  toast.error(event.detail?.message || '连接失败，请重新登录')
  // 延迟跳转到登录页
  setTimeout(() => {
    router.push({ name: 'Login' })
  }, 2000)
}

// 初始化WebSocket连接（用于接收客服消息）
function initServiceSocket() {
  if (!userStore.token) {
    return
  }
  
  const socket = initSocket()
  if (!socket) {
    return
  }
  
  // 移除旧的监听器（避免重复）
  // 注意：不要移除 'user:authenticated'，因为心跳机制依赖它
  // socket.off('user:authenticated')  // 注释掉，保留心跳机制
  socket.off('client:new_reply')
  socket.off('connect_error')
  socket.off('disconnect')
  
  // 监听认证成功（添加额外的监听器，不覆盖原有的）
  // 原有的 user:authenticated 监听器在 socket.js 中，用于启动心跳
  socket.on('user:authenticated', (data) => {
    console.log('客服系统WebSocket认证成功:', data)
    // 认证成功后，触发事件通知Service页面加载未读消息
    window.dispatchEvent(new CustomEvent('service:authenticated'))
  })
  
  // 监听连接成功，确保认证
  socket.on('connect', () => {
    console.log('客服系统WebSocket连接成功，发送认证')
    // 重新发送认证
    socket.emit('user:authenticate', {
      token: userStore.token
    })
  })
  
  // 监听新回复（客服消息）
  socket.on('client:new_reply', (data) => {
    console.log('收到客服回复:', data)
    // 播放提示音
    playServiceMessageSound()
    // 更新未读消息数
    updateServiceUnreadCount()
    // 触发全局事件，让Service页面监听
    window.dispatchEvent(new CustomEvent('service:new_reply', { detail: data }))
  })
  
  // 监听公告消息
  socket.on('announcement', (encoded) => {
    try {
      // 解码Base64消息（浏览器兼容方式）
      let decoded
      if (typeof encoded === 'string') {
        try {
          // 使用 atob 解码 Base64（返回 Latin-1 字符串）
          const binaryString = atob(encoded)
          
          // 将 Latin-1 字符串转换为字节数组
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          
          // 使用 TextDecoder 将字节数组解码为 UTF-8 字符串
          const decoder = new TextDecoder('utf-8')
          const utf8String = decoder.decode(bytes)
          
          try {
            decoded = JSON.parse(utf8String)
          } catch (parseError) {
            // 不是JSON，使用字符串
            console.warn('公告消息不是有效的JSON:', parseError)
            decoded = utf8String
          }
        } catch (error) {
          console.error('Base64解码失败:', error)
          // 如果解码失败，尝试直接使用原值
          decoded = encoded
        }
      } else {
        decoded = encoded
      }
      
      console.log('收到公告消息:', decoded)
      showAnnouncement(decoded)
    } catch (error) {
      console.error('解析公告消息失败:', error)
    }
  })
  
  // 播放客服消息提示音
  function playServiceMessageSound() {
    try {
      const audio = new Audio('/res/sound/message.mp3')
      audio.volume = 0.5
      audio.play().catch(err => {
        console.warn('播放提示音失败:', err)
      })
    } catch (error) {
      console.warn('创建音频对象失败:', error)
    }
  }
  
  // 更新未读消息数（触发TabBar更新）
  function updateServiceUnreadCount() {
    window.dispatchEvent(new CustomEvent('service:unread_updated'))
  }
  
  // 监听连接错误
  socket.on('connect_error', (error) => {
    console.error('客服系统WebSocket连接错误:', error)
  })
  
  // 监听断开连接（socket.js会处理重连）
  socket.on('disconnect', (reason) => {
    console.log('客服系统WebSocket连接断开:', reason)
  })
  
  // 监听重连中事件
  socket.on('reconnecting', (attemptNumber) => {
    console.log(`WebSocket重连中 (${attemptNumber}/${socket.io.opts.reconnectionAttempts})...`)
  })
  
  // 添加事件监听器（避免重复添加）
  window.removeEventListener('socket:reconnect_failed', handleReconnectFailed)
  window.removeEventListener('socket:require_relogin', handleRequireRelogin)
  window.addEventListener('socket:reconnect_failed', handleReconnectFailed)
  window.addEventListener('socket:require_relogin', handleRequireRelogin)
}

// 处理页面可见性变化（前后台切换）
const handleVisibilityChange = () => {
  if (document.hidden) {
    // 页面切换到后台，暂停BGM（标记为自动恢复）
    if (bgmManager.isPlaying) {
      bgmManager.pause(true) // true表示这是自动暂停，应该自动恢复
    }
  } else {
    // 页面切换到前台，如果应该自动恢复，则恢复BGM播放
    if (bgmManager.shouldResume()) {
      bgmManager.resume()
    }
    // 检查WebSocket连接
    const socket = getSocket()
    if (userStore.token && (!socket || !socket.connected)) {
      initServiceSocket()
    }
  }
}

// 检查今日是否已隐藏该公告
function isAnnouncementHiddenToday(announcementId) {
  if (!announcementId) return false
  const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD
  const key = `announcement_hide_${today}_${announcementId}`
  return localStorage.getItem(key) === 'true'
}

// 显示公告
function showAnnouncement(announcement) {
  if (!announcement || !announcement.id) return
  
  // 检查今日是否已隐藏
  if (isAnnouncementHiddenToday(announcement.id)) {
    console.log('公告今日已隐藏:', announcement.id)
    return
  }
  
  // 如果当前没有显示公告，直接显示
  if (!announcementVisible.value) {
    currentAnnouncement.value = announcement
    announcementVisible.value = true
  } else {
    // 如果正在显示公告，加入队列
    announcementQueue.value.push(announcement)
  }
}

// 处理公告关闭
function handleAnnouncementClose() {
  // 标记为已显示
  if (currentAnnouncement.value.id) {
    shownAnnouncementIds.value.add(currentAnnouncement.value.id)
  }
  
  // 显示队列中的下一条公告
  if (announcementQueue.value.length > 0) {
    currentAnnouncement.value = announcementQueue.value.shift()
    announcementVisible.value = true
  } else {
    announcementVisible.value = false
    currentAnnouncement.value = {}
  }
}

// 加载并显示公告（登录后调用）
async function loadAndShowAnnouncements() {
  try {
    const res = await getPublishedAnnouncements()
    if (res.code === 200 && res.data && res.data.length > 0) {
      // 过滤掉今日已隐藏的公告
      const visibleAnnouncements = res.data.filter(ann => {
        return !isAnnouncementHiddenToday(ann.id) && !shownAnnouncementIds.value.has(ann.id)
      })
      
      if (visibleAnnouncements.length > 0) {
        // 显示第一条公告，其他的加入队列
        currentAnnouncement.value = visibleAnnouncements[0]
        announcementVisible.value = true
        
        if (visibleAnnouncements.length > 1) {
          announcementQueue.value = visibleAnnouncements.slice(1)
        }
      }
    }
  } catch (error) {
    console.error('加载公告失败:', error)
  }
}

// 监听登录状态变化
watch(() => userStore.token, async (newToken, oldToken) => {
  if (newToken && !oldToken) {
    // 用户登录，初始化WebSocket连接
    initServiceSocket()
    // 延迟一下，确保WebSocket连接成功后再加载公告
    setTimeout(() => {
      loadAndShowAnnouncements()
    }, 1000)
  } else if (!newToken && oldToken) {
    // 用户退出登录，断开WebSocket连接
    disconnectSocket()
    // 清空公告相关状态
    announcementVisible.value = false
    currentAnnouncement.value = {}
    announcementQueue.value = []
    shownAnnouncementIds.value.clear()
  }
})

onMounted(() => {
  // 检测设备类型
  detectDevice()
  
  // 监听窗口大小变化
  window.addEventListener('resize', handleResize)
  
  // 监听页面可见性变化
  document.addEventListener('visibilitychange', handleVisibilityChange)
  
  // 如果已登录，初始化WebSocket连接并加载公告
  if (userStore.token) {
    initServiceSocket()
    // 延迟加载公告，确保WebSocket连接成功
    setTimeout(() => {
      loadAndShowAnnouncements()
    }, 1500)
  }
})

onUnmounted(() => {
  // 移除事件监听
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('socket:reconnect_failed', handleReconnectFailed)
  window.removeEventListener('socket:require_relogin', handleRequireRelogin)
})
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

#app {
  width: 100%;
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  padding-bottom: 60px; /* 为底部导航栏留出空间 */
}

/* PC访问时的竖版分辨率锁定 */
#app.pc-viewport {
  display: flex;
  justify-content: center;
  align-items: center;
  background: #0a0e1f;
  padding: 12px;
  overflow: hidden;
}

#app.pc-viewport .pc-stage {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

#app.pc-viewport .pc-scale {
  transform-origin: center center;
  will-change: transform;
}

#app.pc-viewport .pc-container {
  width: 390px;
  height: 844px;
  min-height: 844px;
  max-height: 844px;
  position: relative;
  background: #0b1020;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  /* 模拟移动端滚动 */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

#app.pc-viewport .pc-container::-webkit-scrollbar {
  width: 4px;
}

#app.pc-viewport .pc-container::-webkit-scrollbar-track {
  background: transparent;
}

#app.pc-viewport .pc-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

#app.pc-viewport .pc-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* 页面过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 响应式调整：如果屏幕太小，使用全屏 */
@media (max-width: 480px) {
  #app.pc-viewport {
    align-items: flex-start;
    padding: 0;
  }
  
#app.pc-viewport .pc-stage {
    width: 100%;
    height: auto;
    min-height: 100vh;
    overflow: auto;
  }

  #app.pc-viewport .pc-scale {
    transform: none !important;
  }

  #app.pc-viewport .pc-container {
    width: 100%;
    height: auto;
    min-height: 100vh;
    max-height: none;
    box-shadow: none;
    border-radius: 0;
    border: none;
  }
}
</style>

